import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { categories } from "@/mocks/anime";

const navLinks = [
  { to: "/", label: "首页" },
  { to: "/category/fairy", label: "妖精番剧" },
  { to: "/category/elf", label: "精灵动漫" },
  { to: "/category/monster", label: "妖怪动漫" },
  { to: "/about", label: "关于我们" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [resultOpen, setResultOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/";
  const solid = isHome ? scrolled : true;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setResultOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setResultOpen(false);
  }, [location.pathname]);

  const suggestions = categories.filter((c) =>
    query ? c.name.includes(query) || c.tagline.includes(query) : true
  );

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const term = query.trim();
    if (term) {
      setResultOpen(false);
      navigate(`/?q=${encodeURIComponent(term)}`);
    }
  };

  const textColor = solid ? "text-foreground-900" : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-18">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span
            className={`w-9 h-9 rounded-full flex items-center justify-center text-lg transition-colors ${
              solid ? "bg-primary-500 text-background-50" : "bg-white/20 text-white"
            }`}
          >
            <i className="ri-sparkling-2-fill"></i>
          </span>
          <span className={`font-heading text-xl md:text-2xl ${textColor}`}>
            妖精动漫网
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {navLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                location.pathname === l.to || (l.to !== "/" && location.pathname.startsWith(l.to))
                  ? solid
                    ? "bg-primary-500 text-background-50"
                    : "bg-white/20 text-white"
                  : solid
                  ? "text-foreground-700 hover:text-primary-600"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div ref={searchRef} className="hidden md:block relative">
          <form onSubmit={onSubmit}>
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setResultOpen(true);
              }}
              onFocus={() => setResultOpen(true)}
              placeholder="搜番剧，如：夏目友人帐"
              className={`w-52 lg:w-64 h-10 pl-4 pr-10 rounded-full text-sm outline-none transition-colors ${
                solid
                  ? "bg-background-100 border border-background-200 text-foreground-900 placeholder-foreground-400 focus:border-primary-400"
                  : "bg-white/15 border border-white/30 text-white placeholder-white/70 focus:border-white/60"
              }`}
            />
            <button
              type="submit"
              aria-label="搜索"
              className={`absolute right-1 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full flex items-center justify-center ${
                solid ? "text-foreground-500 hover:text-primary-600" : "text-white/80"
              }`}
            >
              <i className="ri-search-line text-base"></i>
            </button>
          </form>
          {resultOpen && query && (
            <div className="absolute right-0 top-12 w-64 bg-background-50 border border-background-200 rounded-xl overflow-hidden shadow-card">
              {suggestions.length > 0 ? (
                suggestions.map((c) => (
                  <button
                    key={c.type}
                    type="button"
                    onClick={() => navigate(`/category/${c.type}`)}
                    className="w-full text-left px-4 py-3 hover:bg-background-100 flex items-center gap-3 cursor-pointer"
                  >
                    <span className="w-7 h-7 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                      <i className={c.icon}></i>
                    </span>
                    <span className="text-sm text-foreground-800">{c.name}</span>
                  </button>
                ))
              ) : (
                <p className="px-4 py-3 text-sm text-foreground-500">没有匹配的栏目，按回车搜索</p>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="菜单"
          className={`lg:hidden w-10 h-10 rounded-lg flex items-center justify-center text-xl ${
            solid ? "text-foreground-800" : "text-white"
          }`}
        >
          <i className={menuOpen ? "ri-close-line" : "ri-menu-line"}></i>
        </button>
      </nav>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-3">
          <div className="mb-3 md:hidden">
            <form onSubmit={onSubmit} className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜番剧"
                className="w-full h-11 pl-4 pr-11 rounded-full text-sm bg-background-100 border border-background-200 outline-none focus:border-primary-400"
              />
              <button
                type="submit"
                aria-label="搜索"
                className="absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center text-foreground-500"
              >
                <i className="ri-search-line"></i>
              </button>
            </form>
          </div>
          <div className="flex flex-col gap-1">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-4 py-3 rounded-lg text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}